import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { EDocumentType } from '@/utils/constants'

import { IHTMLCompareChangeItem } from './html/types'
import { IPDFCompareChangeItem } from './pdf/types'
import {
  EDocumentComparisonChangeType,
  EDocumentComparisonStatus,
  VERSION_COMPARISON_DEFAULT_PAGE,
  VERSION_COMPARISON_ZOOM_DEFAULT,
} from './constants'
import { TSelectedVersion } from './types'
import { computeTotalPages } from './utils'

interface IVersionComparisonState {
  versionSelectionDialogOpen: boolean
  documentId: string | null
  documentFilename: string | null
  documentType: EDocumentType
  compareVersions: TSelectedVersion[] | null
  filteredStatuses: EDocumentComparisonChangeType[]
  zoomScale: number
  currentPage: number
  totalPages: number
  compareDocumentStatus: EDocumentComparisonStatus | null
  compareDocumentDiff: IHTMLCompareChangeItem[] | IPDFCompareChangeItem[] | null
  focusedChangeUuid: string | null
}

interface IVersionComparisonStore extends IVersionComparisonState {
  setVersionSelectionDialogOpen: (open?: boolean) => void
  setCompareVersionDocument: (payload?: {
    documentId: string
    documentType?: EDocumentType
    documentFilename?: string
  }) => void
  setCompareVersions: (versions?: TSelectedVersion[]) => void
  setCompareVersionParserUrl: (versionId: string, parserUrl: string) => void
  setFilteredStatuses: (statuses?: EDocumentComparisonChangeType[]) => void
  setZoomScale: (scale: number) => void
  setVersionReady: (versionId: string, isReady: boolean) => void
  setCurrentPage: (page: number) => void
  setTotalPages: (versionId: string, totalPages: number) => void
  setCompareResult: (payload: {
    status?: EDocumentComparisonStatus
    diff?: IHTMLCompareChangeItem[] | IPDFCompareChangeItem[]
  }) => void
  setFocusedChangeUuid: (uuid?: string) => void
  reset: () => void
}

const initialState: IVersionComparisonState = {
  versionSelectionDialogOpen: false,
  documentId: null,
  documentFilename: null,
  documentType: EDocumentType.HTML,
  compareVersions: null,
  filteredStatuses: [],
  zoomScale: VERSION_COMPARISON_ZOOM_DEFAULT,
  currentPage: VERSION_COMPARISON_DEFAULT_PAGE,
  totalPages: VERSION_COMPARISON_DEFAULT_PAGE,
  compareDocumentStatus: EDocumentComparisonStatus.COMPARING,
  compareDocumentDiff: null,
  focusedChangeUuid: null,
}

export const useVersionComparisonStore = create<IVersionComparisonStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Set version selection dialog open/close state
       */
      setVersionSelectionDialogOpen: (open = false) => {
        set(
          { versionSelectionDialogOpen: open },
          false,
          'setVersionSelectionDialogOpen'
        )
      },

      /**
       * Set compare version document
       */
      setCompareVersionDocument: (payload?: {
        documentId: string
        documentType?: EDocumentType
        documentFilename?: string
      }) => {
        const docId = payload?.documentId ?? null
        const docType = payload?.documentType ?? EDocumentType.HTML
        const docFilename = payload?.documentFilename ?? null
        set(
          {
            documentId: docId,
            documentFilename: docFilename,
            documentType: docType,
            versionSelectionDialogOpen: !!docId,
          },
          false,
          'setCompareVersionDocument'
        )
      },

      /**
       * Set compare versions
       */
      setCompareVersions: (versions?: TSelectedVersion[]) => {
        const safeVersions = versions ?? []
        const nextGlobalTotal = computeTotalPages(safeVersions)

        set(
          {
            compareVersions: safeVersions,
            totalPages: nextGlobalTotal,
            currentPage: Math.min(get().currentPage, nextGlobalTotal),
          },
          false,
          'setCompareVersions'
        )
      },

      /**
       * Set compare version
       */
      setCompareVersionParserUrl: (versionId: string, parserUrl: string) => {
        const { compareVersions } = get()
        if (!compareVersions) return
        const updatedVersions = compareVersions.map((v) =>
          v.id === versionId ? { ...v, parserUrl } : v
        )
        set(
          { compareVersions: updatedVersions },
          false,
          'setCompareVersionParserUrl'
        )
      },

      /**
       * Set filtered statuses
       */
      setFilteredStatuses: (statuses?: EDocumentComparisonChangeType[]) => {
        set({ filteredStatuses: statuses ?? [] }, false, 'setFilteredStatuses')
      },

      /**
       * Set zoom scale
       */
      setZoomScale: (scale: number) => {
        set({ zoomScale: scale }, false, 'setZoomScale')
      },

      /**
       * Set total pages and recalculate total/current page
       */
      setTotalPages: (versionId: string, totalPages: number) => {
        set(
          (state) => {
            if (!state.compareVersions) return state

            const updatedVersions = state.compareVersions.map((version) =>
              version.id === versionId ? { ...version, totalPages } : version
            )

            const nextGlobalTotal = computeTotalPages(updatedVersions)

            return {
              compareVersions: updatedVersions,
              totalPages: nextGlobalTotal,
              currentPage: Math.min(state.currentPage, nextGlobalTotal),
            }
          },
          false,
          'setTotalPages'
        )
      },

      /**
       * Set current page (clamped to available total pages)
       */
      setCurrentPage: (page: number) => {
        const maxPage = Math.max(
          get().totalPages,
          VERSION_COMPARISON_DEFAULT_PAGE
        )
        const nextPage = Math.min(
          Math.max(page, VERSION_COMPARISON_DEFAULT_PAGE),
          maxPage
        )

        set({ currentPage: nextPage }, false, 'setCurrentPage')
      },

      /**
       * Set version ready state
       */
      setVersionReady: (versionId: string, isReady: boolean) => {
        set(
          (state) => {
            if (!state.compareVersions) {
              return state
            }

            const updatedVersions = state.compareVersions.map((version) =>
              version.id === versionId ? { ...version, isReady } : version
            )

            return { compareVersions: updatedVersions }
          },
          false,
          'setVersionReady'
        )
      },

      /**
       * Set compare result
       */
      setCompareResult: (payload: {
        status?: EDocumentComparisonStatus
        diff?: IHTMLCompareChangeItem[] | IPDFCompareChangeItem[]
      }) => {
        const { compareDocumentStatus, compareDocumentDiff } = get()
        set(
          {
            compareDocumentStatus: payload.status ?? compareDocumentStatus,
            compareDocumentDiff: payload.diff ?? compareDocumentDiff,
          },
          false,
          'setCompareResult'
        )
      },

      /**
       * Set focused change UUID
       */
      setFocusedChangeUuid: (uuid: string | null) => {
        set({ focusedChangeUuid: uuid }, false, 'setFocusedChangeUuid')
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    {
      name: 'Ascend Vista UI - Version Comparison Store',
    }
  )
)
